import { useEffect, useState } from "preact/hooks";
import { SpecInput } from "./components/SpecInput.js";
import { PipelineCanvas, emptyPipelineState, type PipelineState } from "./components/PipelineCanvas.js";
import { ArtifactLedger } from "./components/ArtifactLedger.js";
import { AgentChat, type AgentToolSpec } from "./components/AgentChat.js";
import { runBuild, type BuildResult, type PipelineArtifact } from "./builder.js";
import { track } from "./telemetry.js";

type Stage = "input" | "building" | "ready" | "error";

export function App() {
  const [stage, setStage] = useState<Stage>("input");
  const [pipeline, setPipeline] = useState<PipelineState>(emptyPipelineState());
  const [artifacts, setArtifacts] = useState<PipelineArtifact[]>([]);
  const [result, setResult] = useState<BuildResult | undefined>();
  const [errorMsg, setErrorMsg] = useState<string | undefined>();
  // The agent autofires one provocative turn once. autoTurn is consumed by AgentChat
  // via a fresh remount (key changes when result changes).
  const [autoTurn, setAutoTurn] = useState<string | undefined>();

  const onSubmit = async (spec: unknown, label: string): Promise<void> => {
    track({ event: "scorecard.run", props: { chip: "pasted" } });
    setStage("building");
    setErrorMsg(undefined);
    setPipeline(emptyPipelineState());
    setArtifacts([]);
    let st = emptyPipelineState();
    const arts: PipelineArtifact[] = [];
    try {
      const gen = runBuild(spec);
      for (;;) {
        const next = await gen.next();
        if (next.done) {
          setResult(next.value);
          setStage("ready");
          track({ event: "scorecard.grade", props: { grade: "A" } });
          // Pick a provocative auto-turn based on what the build produced.
          const hasDestructive = next.value.design.tools.some((t) => t.annotations.destructiveHint);
          setAutoTurn(
            hasDestructive
              ? "List a couple of items, then show me what would happen if I asked you to delete one. Don't actually delete anything."
              : "Show me what you can do — list a few items from the most interesting tool."
          );
          return;
        }
        const ev = next.value;
        st = {
          stage: { ...st.stage, [ev.stage]: ev.state },
          facts: ev.fact ? { ...st.facts, [ev.stage]: ev.fact } : st.facts
        };
        setPipeline(st);
        if (ev.artifact) {
          arts.push(ev.artifact);
          setArtifacts([...arts]);
        }
      }
    } catch (e) {
      setErrorMsg((e as Error).message);
      setStage("error");
    }
    void label;
  };

  const tools: AgentToolSpec[] = result
    ? result.design.tools.map((t) => ({
        name: t.name.slice(0, 64),
        description: `${t.title}. ${t.description}`.slice(0, 1024),
        input_schema: t.inputSchema
      }))
    : [];

  const systemPrompt = result
    ? [
        `You are an agent driving an MCP server freshly built from "${result.apiTitle}".`,
        `It has ${result.toolCount} tools (listed in the tools schema).`,
        `Use the tools to answer the user. Prefer concrete tool calls over guesses.`,
        `If a tool returns an error, explain plainly what happened and pick a different approach.`,
        `Keep answers short and direct — booth scope.`
      ].join(" ")
    : "";

  return (
    <main style={{ maxWidth: 880, marginInline: "auto", padding: "24px 16px 80px" }}>
      {stage === "input" && <SpecInput onSubmit={onSubmit} />}

      {(stage === "building" || stage === "ready" || stage === "error") && (
        <section>
          <PipelineCanvas state={pipeline} />
          {stage === "error" && errorMsg && (
            <p style={{ color: "#e57a7a", fontSize: 13, textAlign: "center", marginTop: 8 }}>
              build failed — {errorMsg}
            </p>
          )}
          {stage === "ready" && result && (
            <div style={{ textAlign: "center", marginTop: 6, fontFamily: "monospace", fontSize: 12 }}>
              <div style={{ color: "#7ad48c" }}>
                ✓ {result.apiTitle} — {result.toolCount} tools, {result.redteamBlockedPct}% of attacks blocked
              </div>
              <div className="dim" style={{ marginTop: 2 }}>
                digest {result.serverDigest.slice(0, 12)}
              </div>
            </div>
          )}
          <ArtifactLedger artifacts={artifacts} />
        </section>
      )}

      {stage === "ready" && result && (
        <AgentChat
          key={result.serverDigest}
          tools={tools}
          systemPrompt={systemPrompt}
          callTool={async (name, args): Promise<unknown> => result.runtime.call(name, args)}
          autoFirstTurn={autoTurn}
          greeting={`Built ${result.toolCount} tools from "${result.apiTitle}". Watch the demo turn below, then try your own prompt.`}
        />
      )}

      {(stage === "ready" || stage === "error") && (
        <div style={{ marginTop: 24, textAlign: "center" }}>
          <button
            className="secondary"
            onClick={(): void => {
              setStage("input");
              setResult(undefined);
              setArtifacts([]);
              setAutoTurn(undefined);
              setPipeline(emptyPipelineState());
            }}
          >
            ← Build another
          </button>
        </div>
      )}
    </main>
  );
}

// Unused-import guard; satisfies the linter when useEffect isn't wired yet.
void useEffect;
